import { bootstrapCameraKit, createMediaStreamSource, Transform2D } from '@snap/camera-kit';
import {createContext, useEffect, useRef, useState} from 'react';
import keys from '../keys.json';
import Camera from "../libs/camera";
import {ORIENTATION} from "../constants";

const apiToken = keys.STAGING_API_KEY;
const lensGroupId = keys.LENS_GROUP_ID;

export const CameraKitContext = createContext(null);

export const CameraKit = ({ canvasRef, showDebug, orientation, children, onChangeCameraFacing }) => {
    const camera = useRef(null);
    const [isStarting, setIsStarting] = useState(true);
    const [lenses, setLenses] = useState([]);
    const [isApplyingLens, setIsApplyingLens] = useState(false);
    const [lensIndex, setLensIndex] = useState(0);
    const [cameraFacing, setCameraFacing] = useState(Camera.FRONT_CAMERA);

    useEffect(() => {
        if (camera.current) return showDebug('Camera is already created');
        camera.current = new Camera(showDebug);
        const config = {
            apiToken: apiToken,
            lensGroupId: lensGroupId,
            canvasRef: canvasRef.current,
            cameraFacing: cameraFacing,
            lensIndex: lensIndex,
        };
        setIsStarting(true);
        camera.current.initialize(config)
            .then(cam => setLenses(cam.lenses))
            .catch(err => showDebug(err.message))
            .finally(() => setIsStarting(false));
    }, []);

    function changeLens(lensIndex) {
        setLensIndex(lensIndex);
    }

    function toggleCamera() {
        const facing = cameraFacing === Camera.FRONT_CAMERA
            ? Camera.BACK_CAMERA
            : Camera.FRONT_CAMERA;
        setCameraFacing(facing);
    }

    useEffect(() => {
        if (!camera.current) return;
        setIsApplyingLens(true);
        camera.current.applyLens(lensIndex)
            .catch(err => {
                showDebug(err.message);
                console.error(err);
            })
            .finally(() => setIsApplyingLens(false));
    }, [ lensIndex ]);

    useEffect(() => {
        if (!camera.current) return;
        camera.current.changeCameraFacing(cameraFacing)
            .then(() => showDebug(`Camera has been changed to ${cameraFacing}`))
            .then(() => onChangeCameraFacing(cameraFacing))
            .catch(err => {
                showDebug(err.message);
                console.error(err);
            });
    }, [ cameraFacing ]);

    useEffect(() => {
        if (!camera.current) return;
        if (orientation === ORIENTATION.PORTRAIT) {
            camera.current.resume().catch(showDebug);
        } else if (orientation === ORIENTATION.LANDSCAPE) {
            camera.current.pause().catch(showDebug);
        }
    }, [ orientation ]);

    useEffect(() => {
        if (!camera.current) return;

        const handleVisibilityChange = async () => {
            if (!camera.current) return;
            if (document.hidden) {
                await camera.current.pause().catch(showDebug);
                return;
            }

            await camera.current.resume().catch(async (err) => {
                showDebug(`Camera resume failed: ${err?.message || err}`);
                try {
                    await camera.current.recover();
                    await camera.current.applyLens(lensIndex);
                } catch (recoverError) {
                    showDebug(`Camera recovery failed: ${recoverError?.message || recoverError}`);
                }
            });
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pageshow', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pageshow', handleVisibilityChange);
        };
    }, [ lensIndex ]);

    return <CameraKitContext.Provider value={{
        lenses, lensIndex, showDebug, isApplyingLens, isStarting, changeLens, toggleCamera,
    }}>
        {children}
    </CameraKitContext.Provider>;
}
